import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  Tooltip, 
  MenuItem
} from "@mui/material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Logo from "./assets/logo2.png";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";


/* ===== CONSTANTS ===== */
const COST_PER_1000 = 2.7;
const ONE_TIME_COST = 5500;

const COLORS = {
  black: "#000000",
  yellow: "#F3C26B",
  lightYellow: "#FBE2B3",
  inputBg: "#FFF6E1",
  border: "#D9A441"
};

export default function IICostSavings() {
  /* ===== PERSONAL INFO ===== */
  const [company, setCompany] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
const [followUpError, setFollowUpError] = useState("");
  const [discussionWith, setDiscussionWith] = useState(""); 

  const [errors, setErrors] = useState({});

  const [customerId, setCustomerId] = useState(null);


const [showSubmit, setShowSubmit] = useState(false);
const [isSubmitting, setIsSubmitting] = useState(false);
const [hasCalculated, setHasCalculated] = useState(false);
const [requestFollowUp, setRequestFollowUp] = useState("Yes");

const handleRecalculate = () => {
  // Clear personal info
  setCompany("");
  setFirstName("");
  setLastName("");
  setEmail("");
  setPhone("");
  setDiscussionWith("");
  setRequestFollowUp("Yes");

  // Clear calculator
  setGpm("");

  // Reset UI states
  setShowResult(false);
  setHasCalculated(false);
  setIsSubmitting(false);
  setErrors({});
};


  const validateFields = () => {
  const newErrors = {};

  // Company: letters, numbers, spaces, special chars
  if (!company.trim()) {
    newErrors.company = "Company is required";
  }

  // First Name: letters & spaces only
  if (!firstName.trim()) {
    newErrors.firstName = "First name is required";
  } else if (!/^[A-Za-z\s]+$/.test(firstName)) {
    newErrors.firstName = "Only letters and spaces allowed";
  }

  // Last Name: letters & spaces only
  if (!lastName.trim()) {
    newErrors.lastName = "Last name is required";
  } else if (!/^[A-Za-z\s]+$/.test(lastName)) {
    newErrors.lastName = "Only letters and spaces allowed";
  }

  // Email
  if (!email.trim()) {
    newErrors.email = "Email is required";
  } else if (
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  ) {
    newErrors.email = "Enter a valid email address";
  }

  // Phone: numbers, +, -, (), spaces
  if (!phone.trim()) {
    newErrors.phone = "Phone number is required";
  } else if (
    !/^[0-9+\-\s()]+$/.test(phone)
  ) {
    newErrors.phone = "Invalid phone number format";
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};


  /* ===== CALCULATOR ===== */
  const [gpm, setGpm] = useState("");
  const [showResult, setShowResult] = useState(false);


  const isFormValid =
  company.trim() &&
  firstName.trim() &&
  lastName.trim() &&
  email.trim() &&
  phone.length === 10 &&
  gpm &&
  // requestFollowUp === "Yes" &&
  Object.values(errors).every((err) => !err);

  const canCalculate =
    company && firstName && lastName && email && phone && gpm;

  /* ===== CALCULATIONS ===== */
  const gpm1 = Number(gpm);
  const gpm2 = gpm1 * 60;
  const gpm3 = gpm2 * 24;
  const gpm4 = (gpm3 * 365) / 12;
  const gpm5 = gpm4 * 12;

  const cost1 = (gpm1 / 1000) * COST_PER_1000;
  const cost2 = (gpm2 / 1000) * COST_PER_1000;
  const cost3 = (gpm3 / 1000) * COST_PER_1000;
  const cost4 = (gpm4 / 1000) * COST_PER_1000;
  const cost5 = (gpm5 / 1000) * COST_PER_1000;

  const roi = ((cost5 - ONE_TIME_COST) / ONE_TIME_COST) * 100;
  const paybackMonths = ONE_TIME_COST / cost4;

  const inputSX = {
    mb: 3,
    backgroundColor: COLORS.inputBg,
    borderRadius: "6px",
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: COLORS.border },
      "&:hover fieldset": { borderColor: COLORS.yellow },
      "&.Mui-focused fieldset": {
        borderColor: COLORS.black,
        borderWidth: "2px"
      }
    }
  };

  const formatNumber = (value) =>
  Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

const downloadPDF = async () => {
  if (!customerId) {
    swal("Please calculate results first");
    return;
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_SERVER_URL}/savyandsons/DownloadIICostSavingsReport/${customerId}`,
      {
        method: "GET"
      }
    );

    if (!response.ok) {
      throw new Error("PDF download failed");
    }

    const blob = await response.blob();

    // Create browser download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    // Filename (optional override)
    link.download = "II_Cost_Savings_Report.pdf";

    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

  } catch (error) {
    console.error(error);
    swal(
      "Error",
      "Unable to download the PDF at this time. Please try again later.",
      "error"
    );
  }
};


  /* ===== PDF DOWNLOAD ===== */
//    const downloadPDF = () => {
//   const doc = new jsPDF("landscape", "pt", "A4");

//   /* ===== LOGO (LEFT) ===== */
//  /* ===== LOGO (LEFT – FIXED ASPECT) ===== */
// const imgProps = doc.getImageProperties(Logo);
// const logoWidth = 160;
// const logoHeight = (imgProps.height * logoWidth) / imgProps.width;

// doc.addImage(
//   Logo,
//   "PNG",
//   40,
//   30,
//   logoWidth,
//   logoHeight
// );


//   /* ===== TITLE ===== */
//   doc.setFontSize(22);
//   doc.text("I&I Cost Savings", 420, 50, { align: "center" });

//   // doc.setFontSize(14);
//   // doc.text("2 manholes waterproofed", 420, 80, { align: "center" });

//   /* ===== RATE TABLE ===== */
//   doc.autoTable({
//     startY: 160,
//     theme: "grid",
//     head: [[
//       "Rate of Infiltration\n(gallons per minute)",
//       "Rate of Infiltration\n(gallons per hour)",
//       "Rate of Infiltration\n(gallons per day)",
//       "Rate of Infiltration\n(gallons per month)",
//       "Rate of Infiltration\n(gallons per year)"
//     ]],
//     // body: [[
//     //   gpm1.toFixed(2),
//     //   gpm2.toLocaleString(),
//     //   gpm3.toLocaleString(),
//     //   gpm4.toLocaleString(),
//     //   gpm5.toLocaleString()
//     // ]],
//     body: [[
//   formatNumber(gpm1),
//   formatNumber(gpm2),
//   formatNumber(gpm3),
//   formatNumber(gpm4),
//   formatNumber(gpm5)
// ]],

//     styles: {
//       halign: "center",
//       fontSize: 11,
//       cellPadding: 8
//     },
//     headStyles: {
//       fillColor: [243, 194, 107], // yellow
//       textColor: [0, 0, 0]
//     },
//     bodyStyles: {
//       fillColor: [252, 229, 186]
//     }
//   });

//   /* ===== COST LABEL ===== */
//   doc.setTextColor(255, 0, 0);
//   doc.setFontSize(12);
//   doc.text(
//     "Average cost to treat ($2.70 per 1000 gallons)",
//     420,
//     doc.lastAutoTable.finalY + 25,
//     { align: "center" }
//   );

//   /* ===== COST TABLE ===== */
//   doc.autoTable({
//     startY: doc.lastAutoTable.finalY + 40,
//     theme: "grid",
//     head: [[
//       "Cost to Treat\n($ per minute)",
//       "Cost to Treat\n($ per hour)",
//       "Cost to Treat\n($ per day)",
//       "Cost to Treat\n($ per month)",
//       "Cost to Treat\n($ per year)"
//     ]],
//     // body: [[
//     //   `$${cost1.toFixed(2)}`,
//     //   `$${cost2.toFixed(2)}`,
//     //   `$${cost3.toFixed(2)}`,
//     //   `$${cost4.toFixed(2)}`,
//     //   `$${cost5.toFixed(2)}`
//     // ]],
//     body: [[
//   `$${formatNumber(cost1)}`,
//   `$${formatNumber(cost2)}`,
//   `$${formatNumber(cost3)}`,
//   `$${formatNumber(cost4)}`,
//   `$${formatNumber(cost5)}`
// ]],

//     styles: {
//       halign: "center",
//       fontSize: 11,
//       textColor: [255, 0, 0]
//     },
//     headStyles: {
//       fillColor: [255, 242, 204],
//       textColor: [0, 0, 0]
//     },
//     bodyStyles: {
//       fillColor: [255, 249, 220]
//     }
//   });

//   /* ===== SUMMARY TABLE ===== */
//   doc.autoTable({
//     startY: doc.lastAutoTable.finalY + 40,
//     theme: "grid",
//     head: [[
//       "Average Cost for Savy & Sons to Stop Infiltration",
//       "ROI for Year 1",
//       "Investment Return Timing (months)"
//     ]],
//     body: [[
//     `$${ONE_TIME_COST.toLocaleString(undefined, {
//     minimumFractionDigits: 2,
//     maximumFractionDigits: 2
//   })}\nOne Time Cost`,
//       `${roi.toFixed(2)}%`,
//       paybackMonths.toFixed(2)
//     ]],
//     styles: {
//       halign: "center",
//       fontSize: 14,
//       cellPadding: 12
//     },
//     headStyles: {
//       fillColor: [155, 209, 139]
//     },
//     columnStyles: {
//       2: { fillColor: [110, 166, 242] }
//     }
//   });

//   doc.save("II_Cost_Savings.pdf");
// };


const generatePdfBlob = () => {
  const doc = new jsPDF("landscape", "pt", "A4");
    /* ===== LOGO (LEFT) ===== */
 /* ===== LOGO (LEFT – FIXED ASPECT) ===== */
const imgProps = doc.getImageProperties(Logo);
const logoWidth = 160;
const logoHeight = (imgProps.height * logoWidth) / imgProps.width;

doc.addImage(
  Logo,
  "PNG",
  40,
  30,
  logoWidth,
  logoHeight
);

  /* ===== TITLE ===== */
  doc.setFontSize(22);
  doc.text("I&I Cost Savings", 420, 50, { align: "center" });

  // doc.setFontSize(14);
  // doc.text("2 manholes waterproofed", 420, 80, { align: "center" });

  /* ===== RATE TABLE ===== */
  doc.autoTable({
    startY: 160,
    theme: "grid",
    head: [[
      "Rate of Infiltration\n(gallons per minute)",
      "Rate of Infiltration\n(gallons per hour)",
      "Rate of Infiltration\n(gallons per day)",
      "Rate of Infiltration\n(gallons per month)",
      "Rate of Infiltration\n(gallons per year)"
    ]],
    // body: [[
    //   gpm1.toFixed(2),
    //   gpm2.toLocaleString(),
    //   gpm3.toLocaleString(),
    //   gpm4.toLocaleString(),
    //   gpm5.toLocaleString()
    // ]],
    body: [[
  formatNumber(gpm1),
  formatNumber(gpm2),
  formatNumber(gpm3),
  formatNumber(gpm4),
  formatNumber(gpm5)
]],

    styles: {
      halign: "center",
      fontSize: 11,
      cellPadding: 8
    },
    headStyles: {
      fillColor: [243, 194, 107], // yellow
      textColor: [0, 0, 0]
    },
    bodyStyles: {
      fillColor: [252, 229, 186]
    }
  });

  /* ===== COST LABEL ===== */
  doc.setTextColor(255, 0, 0);
  doc.setFontSize(12);
  doc.text(
    "Average cost to treat ($2.70 per 1000 gallons)",
    420,
    doc.lastAutoTable.finalY + 25,
    { align: "center" }
  );

  /* ===== COST TABLE ===== */
  doc.autoTable({
    startY: doc.lastAutoTable.finalY + 40,
    theme: "grid",
    head: [[
      "Cost to Treat\n($ per minute)",
      "Cost to Treat\n($ per hour)",
      "Cost to Treat\n($ per day)",
      "Cost to Treat\n($ per month)",
      "Cost to Treat\n($ per year)"
    ]],
    // body: [[
    //   `$${cost1.toFixed(2)}`,
    //   `$${cost2.toFixed(2)}`,
    //   `$${cost3.toFixed(2)}`,
    //   `$${cost4.toFixed(2)}`,
    //   `$${cost5.toFixed(2)}`
    // ]],
    body: [[
  `$${formatNumber(cost1)}`,
  `$${formatNumber(cost2)}`,
  `$${formatNumber(cost3)}`,
  `$${formatNumber(cost4)}`,
  `$${formatNumber(cost5)}`
]],

    styles: {
      halign: "center",
      fontSize: 11,
      textColor: [255, 0, 0]
    },
    headStyles: {
      fillColor: [255, 242, 204],
      textColor: [0, 0, 0]
    },
    bodyStyles: {
      fillColor: [255, 249, 220]
    }
  });

  /* ===== SUMMARY TABLE ===== */
  doc.autoTable({
    startY: doc.lastAutoTable.finalY + 40,
    theme: "grid",
    head: [[
      "Average Cost for Savy & Sons to Stop Infiltration",
      "ROI for Year 1",
      "Investment Return Timing (months)"
    ]],
    body: [[
      `$${ONE_TIME_COST.toLocaleString()}\nOne Time Cost`,
      `${roi.toFixed(2)}%`,
      paybackMonths.toFixed(2)
    ]],
    styles: {
      halign: "center",
      fontSize: 14,
      cellPadding: 12
    },
    headStyles: {
      fillColor: [155, 209, 139]
    },
    columnStyles: {
      2: { fillColor: [110, 166, 242] }
    }
  });
  return doc.output("blob");

}

const rateCards = [
  {
    label: "Rate of Infiltration",
    unit: "gallons per minute",
    value: gpm1
  },
  {
    label: "Rate of Infiltration",
    unit: "gallons per hour",
    value: gpm2
  },
  {
    label: "Rate of Infiltration",
    unit: "gallons per day",
    value: gpm3
  },
  {
    label: "Rate of Infiltration",
    unit: "gallons per month",
    value: gpm4
  },
  {
    label: "Rate of Infiltration",
    unit: "gallons per year",
    value: gpm5
  }
];

const costCards = [
  {
    label: "Cost to Treat",
    unit: "$ per minute",
    value: cost1
  },
  {
    label: "Cost to Treat",
    unit: "$ per hour",
    value: cost2
  },
  {
    label: "Cost to Treat",
    unit: "$ per day",
    value: cost3
  },
  {
    label: "Cost to Treat",
    unit: "$ per month",
    value: cost4
  },
  {
    label: "Cost to Treat",
    unit: "$ per year",
    value: cost5
  }
];

// const handleCalculateAndSubmit = async () => {
//   if (!isFormValid) {
//     swal("Please complete all required fields");
//     return;
//   }

//   setIsSubmitting(true);
//   setShowResult(true); // show UI results

//   try {
//     // 1️⃣ Generate PDF
//     const pdfBlob = generatePdfBlob();
//     if (!(pdfBlob instanceof Blob)) {
//       throw new Error("PDF generation failed");
//     }

//     // 2️⃣ Customer payload
//     const customerData = {
//       company,
//       firstName,
//       lastName,
//       email,
//       phone,
//       discussionWith
//     };

//     const formData = new FormData();
//     formData.append("customerData", JSON.stringify(customerData));
//     formData.append("pdf", pdfBlob, "II_Cost_Savings.pdf");

//     // 3️⃣ Insert customer
//     const customerRes = await fetch(
//       `${process.env.REACT_APP_SERVER_URL}/savyandsons/insertcustomer`,
//       {
//         method: "POST",
//         body: formData
//       }
//     );

//     if (!customerRes.ok) {
//       throw new Error("Customer insert failed");
//     }

//     const customerResult = await customerRes.json();
//     const customerId = customerResult.id;

//     // 4️⃣ Insert I&I Cost Savings
//     const iiPayload = buildIICostPayload(customerId);

//     const iiRes = await fetch(
//       `${process.env.REACT_APP_SERVER_URL}/savyandsons/insertIICostSavingsDetails/${customerId}`,
//       {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify(iiPayload)
//       }
//     );

//     if (!iiRes.ok) {
//       throw new Error("I&I Cost Savings insert failed");
//     }

//     //  5️⃣ Send Data to Zapier → HubSpot
//       //  (NON-BLOCKING)

//     const zapierPayload = {
//       company,
//       firstName,
//       lastName,
//       email,
//       phone,

//       // I&I Cost Savings Fields
//       // annualSavings: iiPayload.annualSavings,
//       // monthlySavings: iiPayload.monthlySavings,
//       // roi: iiPayload.roi,
//       // gpm: iiPayload.gpm,
//       // flowRate: iiPayload.flowRate,

//       customerId,
//       source: "I&I Calculator",
//       hadADiscussionWith : discussionWith
//     };

//     try {
//       await fetch(`${process.env.REACT_APP_SERVER_URL}/savyandsons/sendToZapier/`, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json"
//         },
//         body: JSON.stringify(zapierPayload)
//       });
//     } catch (zapErr) {
//       console.warn("Zapier webhook failed, continuing app flow", zapErr);
//     }
    
//     swal(
//       "Success",
//       "Your cost savings have been calculated, submitted, and emailed successfully.",
//       "success"
//     );
//      setHasCalculated(true);
//   } catch (err) {
//     console.error(err);
//     swal("Error", err.message || "Submission failed");
//   } finally {
//     setIsSubmitting(false);
//   }
// };

const handleCalculateAndSubmit = async () => {
  if (!isFormValid) {
    swal("Please complete all required fields");
    return;
  }
// if (requestFollowUp !== "Yes") {
//   swal("Please confirm if you would like a specialist to contact you.");
//   return;
// }
  setIsSubmitting(true);
  setShowResult(true);

  try {
    // 1️⃣ Insert Customer
    const customerPayload = {
      company,
      firstName,
      lastName,
      email,
      phone,
      discussionWith,
      requestFollowUp
    };

    const customerRes = await fetch(
      `${process.env.REACT_APP_SERVER_URL}/savyandsons/insertcustomer`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(customerPayload)
      }
    );

    if (!customerRes.ok) {
      throw new Error("Customer insert failed");
    }

    const customerResult = await customerRes.json();
    const customerId = customerResult.id;
setCustomerId(customerResult.id);
    // 2️⃣ Insert I&I Cost Savings
    const iiPayload = buildIICostPayload(customerId);

    const iiRes = await fetch(
      `${process.env.REACT_APP_SERVER_URL}/savyandsons/insertIICostSavingsDetails/${email}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(iiPayload)
      }
    );

    if (!iiRes.ok) {
      throw new Error("I&I Cost Savings insert failed");
    }

   // ===== 3️⃣ GENERATE PDF & EMAIL (BACKEND) =====
    let pdfRes;
    try {
      pdfRes = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/savyandsons/generateAndSendIICostSavingsReport/${customerId}`,
        { method: "POST" }
      );
    } catch (networkErr) {
      throw new Error("Failed to generate or email the report.");
    }

    if (!pdfRes.ok) {
      const text = await pdfRes.text();
      throw new Error(text || "PDF generation/email failed.");
    }

    // 4️⃣ Zapier (non-blocking)
    // try {
    //   await fetch(`${process.env.REACT_APP_SERVER_URL}/savyandsons/sendToZapier/`, {
    //     method: "POST",
    //     headers: { "Content-Type": "application/json" },
    //     body: JSON.stringify({
    //       company,
    //       firstName,
    //       lastName,
    //       email,
    //       phone,
    //       customerId,
    //       source: "I&I Calculator",
    //       hadADiscussionWith: discussionWith
    //     })
    //   });
    // } catch (zapErr) {
    //   console.warn("Zapier failed, continuing", zapErr);
    // }

    // swal(
    //   "Success",
    //   "Your Customer & Cost Savings results are ready. Scroll down to view them, check your email for a copy, or download the PDF.",
    //   "success"
    // );
swal(
  "Calculation Complete",
  "✔ Your I&I cost savings are ready.\n\n" +
  "Next steps:\n" +
  "• Scroll down to view your results\n" +
  "• Download the PDF report\n" +
  "• Check your email for a copy (PDF attached)",
  "success"
);

    setHasCalculated(true);

  } catch (err) {
    console.error(err);
    swal("Error", err.message || "Submission failed");
  } finally {
    setIsSubmitting(false);
  }
};

const round2 = (num) =>
  Number(Number(num).toFixed(2));


// const buildIICostPayload = (customerId) => ([
//   {
//     customerId,
//     rateInfiltrationGpmi: gpm1,
//     rateInfiltrationGph: gpm2,
//     rateInfiltrationGpd: gpm3,
//     rateInfiltrationGpmo: gpm4,
//     rateInfiltrationGpy: gpm5,
//     costTreatPerMinute: cost1,
//     costTreatPerHour: cost2,
//     costTreatPerDay: cost3,
//     costTreatPerMonth: cost4,
//     costTreatPerYear: cost5,
//     ssAverageCost: ONE_TIME_COST,
//     yearOneRoi: roi,
//     investmentReturnTiming: paybackMonths
//   }
// ]);
const buildIICostPayload = (customerId) => ([
  {
    customerId,

    // ===== RATE OF INFILTRATION =====
    rateInfiltrationGpmi: round2(gpm1),
    rateInfiltrationGph: round2(gpm2),
    rateInfiltrationGpd: round2(gpm3),
    rateInfiltrationGpmo: round2(gpm4),
    rateInfiltrationGpy: round2(gpm5),

    // ===== COST TO TREAT =====
    costTreatPerMinute: round2(cost1),
    costTreatPerHour: round2(cost2),
    costTreatPerDay: round2(cost3),
    costTreatPerMonth: round2(cost4),
    costTreatPerYear: round2(cost5),

    // ===== SUMMARY =====
    ssAverageCost: round2(ONE_TIME_COST),
    yearOneRoi: round2(roi),
    investmentReturnTiming: round2(paybackMonths)
  }
]);


const handleSubmit = async () => {
  if (!isFormValid) {
    swal("Please complete all required fields");
    return;
  }
  setIsSubmitting(true); 
  try {
    // 1️⃣ Generate PDF
    const pdfBlob = generatePdfBlob();

if (!(pdfBlob instanceof Blob)) {
  throw new Error("PDF generation failed");
}
    // 2️⃣ Build customer payload
    const customerData = {
      company,
      firstName,
      lastName,
      email,
      phone
    };

    // 3️⃣ Send customer + PDF
    const formData = new FormData();
    formData.append(
      "customerData",
      JSON.stringify(customerData)
    );
    formData.append(
      "pdf",
      pdfBlob,
      "II_Cost_Savings.pdf"
    );

    const customerRes = await fetch(
      `${process.env.REACT_APP_SERVER_URL}/savyandsons/insertcustomer`,
      {
        method: "POST",
        body: formData
      }
    );

    if (!customerRes.ok) {
      throw new Error("Customer insert failed");
    }

    const customerResult = await customerRes.json();
    const customerId = customerResult.id;

    // 4️⃣ Insert I&I cost savings
    const iiPayload = buildIICostPayload(customerId);

    const iiRes = await fetch(
      `${process.env.REACT_APP_SERVER_URL}/savyandsons/insertIICostSavingsDetails/${customerId}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(iiPayload)
      }
    );

    if (!iiRes.ok) {
      throw new Error("I&I Cost Savings insert failed");
    }

    swal("Success", "Your Customer & Cost Savings results are ready. Scroll down to view them, check your email for a copy, or download the PDF", "success");
setIsSubmitting(false); 
  } catch (err) {
    console.error(err);
    swal("Error", err.message || "Submission failed");
  }
};

  return (
   <Box sx={{ background: "#ffffff", minHeight: "100vh", py: 1 }}>
  <Container maxWidth="lg">

    {/* LOGO */}
    {/* <Box textAlign="center" mb={0}>
      <img src={Logo} alt="Logo" style={{ width: 170 }} />
    </Box> */}
    <Box
  textAlign="center"
    mt={{ xs: 3, sm: 4, md: 5 }} 
  mb={{ xs: 2, sm: 3 }}
  px={{ xs: 1, sm: 0 }}
>
  {/* LOGO */}
  <img
    src={Logo}
    alt="Savy & Sons Logo"
    style={{
      width: "clamp(120px, 40vw, 170px)",
      height: "auto",
    }}
  />

  {/* MAIN HEADING */}
  <Typography
    sx={{
      mt: { xs: 1.5, sm: 2 },
      fontSize: {
        xs: 18,   // mobile
        sm: 22,   // tablet
        md: 26,   // desktop
      },
      fontWeight: 800,
      color: "#000",
      lineHeight: 1.25,
    }}
  >
    I&amp;I Cost Savings Calculator
  </Typography>

  {/* SUB HEADING */}
  <Typography
    sx={{
      mt: 0.8,
      fontSize: {
        xs: 12.5, // mobile
        sm: 14,   // tablet
        md: 15,   // desktop
      },
      color: "#555",
      maxWidth: 520,
      mx: "auto",
      lineHeight: 1.5,
      px: { xs: 1, sm: 0 },
    }}
  >
    Estimate how much inflow and infiltration is costing your system
  </Typography>
</Box>



    {/* PERSONAL INFO */}
    <Box sx={{ boxShadow: 3, borderRadius: 2, p: 3, mb: 5 }}>
      <Box sx={{
        background: COLORS.yellow,
        textAlign: "center",
        fontWeight: "bold",
        py: 2,
        borderRadius: 1,
        mb: 3
      }}>
        Personal Information
      </Box>

   <TextField
  fullWidth
  required
  disabled={hasCalculated || isSubmitting}   
  label="Company"
  value={company}
  onChange={(e) => {
    const value = e.target.value;

    // Allow letters, numbers, spaces, and common business symbols
    if (/^[A-Za-z0-9\s&.,'()-]*$/.test(value)) {
      setCompany(value);
      setErrors((prev) => ({ ...prev, company: "" }));
    } else {
      setErrors((prev) => ({
        ...prev,
        company: "Invalid characters used"
      }));
    }
  }}
  sx={inputSX}
  error={!!errors.company}
  helperText={errors.company}
/>


<TextField
  fullWidth
  required
  label="First Name"
  disabled={hasCalculated || isSubmitting}   
  value={firstName}
  onChange={(e) => {
    const value = e.target.value;

    // Allow ONLY letters and spaces
    if (/^[A-Za-z\s]*$/.test(value)) {
      setFirstName(value);
      setErrors((prev) => ({ ...prev, firstName: "" }));
    } else if (!/^[A-Za-z\s]+$/.test(value)) {
      setErrors((prev) => ({
        ...prev,
        firstName: "Only letters and spaces allowed"
      }));
    }else {
      // ✅ CLEAR ERROR HERE
      setErrors((prev) => ({ ...prev, firstName: "" }));
    }
  }}
  sx={inputSX}
  error={!!errors.firstName}
  helperText={errors.firstName}
/>


<TextField
  fullWidth
  required
  disabled={hasCalculated || isSubmitting}   
  label="Last Name"
  value={lastName}
  onChange={(e) => {
    const value = e.target.value;

     if (/^[A-Za-z\s]*$/.test(value)) {
      setLastName(value);
      setErrors((prev) => ({ ...prev, lastName: "" }));
    } else if (!/^[A-Za-z\s]+$/.test(value)) {
      setErrors((prev) => ({
        ...prev,
        lastName: "Only letters and spaces allowed"
      }));
    } else {
      // ✅ CLEAR ERROR HERE
      setErrors((prev) => ({ ...prev, lastName: "" }));
    }

    setLastName(value);
  }}
  sx={inputSX}
  error={!!errors.lastName}
  helperText={errors.lastName}
/>



<TextField
  fullWidth
  required
  label="Email"
  value={email}
  disabled={hasCalculated || isSubmitting}   
  onChange={(e) => {
    const value = e.target.value;
    setEmail(value);

    if (!value) {
      setErrors((prev) => ({ ...prev, email: "Email is required" }));
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setErrors((prev) => ({ ...prev, email: "Enter a valid email address" }));
    } else {
      setErrors((prev) => ({ ...prev, email: "" }));
    }
  }}
  sx={inputSX}
  error={!!errors.email}
  helperText={errors.email}
/>


<TextField
  fullWidth
  required
  label="Phone"
  value={phone}
  disabled={hasCalculated || isSubmitting}   
  onChange={(e) => {
    const value = e.target.value;

    // Allow only numbers
    if (/^\d*$/.test(value)) {
      setPhone(value);

      if (!value) {
        setErrors((prev) => ({
          ...prev,
          phone: "Phone number is required"
        }));
      } else if (value.length !== 10) {
        setErrors((prev) => ({
          ...prev,
          phone: "Phone number must be exactly 10 digits"
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          phone: ""
        }));
      }
    }
  }}
  inputProps={{ maxLength: 10 }}
  sx={inputSX}
  error={!!errors.phone}
  helperText={errors.phone}
/>

<TextField
  select
  fullWidth
  disabled={hasCalculated || isSubmitting}   
  label="Had a discussion with (Optional)"
  value={discussionWith}
  onChange={(e) => setDiscussionWith(e.target.value)}
  sx={{
    ...inputSX,
    "& .MuiSelect-select": {
      textAlign: "left"
    }
  }}
>
  <MenuItem value="None">None</MenuItem>
  <MenuItem value="Gino Padewski">Gino Padewski</MenuItem>
  <MenuItem value="Matt Kelly">Matt Kelly</MenuItem>
  <MenuItem value="Marc Burke">Marc Burke</MenuItem>
  <MenuItem value="John Silvestri">John Silvestri</MenuItem>
   {/* <MenuItem value="John Silvestri"> Alex Czerwinski</MenuItem> */}
 
</TextField>




</Box>

    {/* GPM INPUT */}
    <Box sx={{ boxShadow: 3, borderRadius: 2, p: 3 }}>
      <Box sx={{
        background: COLORS.yellow,
        textAlign: "center",
        fontWeight: "bold",
        py: 2,
        borderRadius: 1,
        mb: 3
      }}>
        Rate of Infiltration (gallons per minute)
      </Box>
{/* <TextField
  fullWidth
  required
  type="number"
  label="Rate of Infiltration (gallons per minute)"
  placeholder="Enter gallons per minute"
  value={gpm}
  onChange={(e) => setGpm(e.target.value)}
  sx={inputSX}
/> */}
<TextField
  fullWidth
  required
  label="Rate of Infiltration (gallons per minute)"
  placeholder="Enter gallons per minute"
  value={gpm}
  disabled={hasCalculated || isSubmitting}   // 🔒 lock after calculate
  onChange={(e) => {
    const value = e.target.value;

    // ✅ Allow empty
    if (value === "") {
      setGpm("");
      return;
    }
  if (/^0/.test(value)) {
    return;
  }
    // ✅ Allow ONLY digits (0–4 digits)
    if (/^\d{0,4}$/.test(value)) {
      setGpm(value);
    }
  }}
  inputProps={{
    inputMode: "numeric",     // mobile numeric keyboard
    pattern: "[0-9]*"
  }}
  sx={{
    ...inputSX,
    "& input": {
      MozAppearance: "textfield" // Firefox
    },
    "& input::-webkit-outer-spin-button": {
      WebkitAppearance: "none"
    },
    "& input::-webkit-inner-spin-button": {
      WebkitAppearance: "none"
    }
  }}
/>
<Box
  sx={{
    display: "flex",
    alignItems: "center",
    gap: 1,
    background: "#FFF8E6",
    border: "1px solid #E5C07B",
    borderRadius: 1.5,
    p: { xs: 1, sm: 1.25 },
    maxWidth: 520,
    mx: "auto",
    mb: 2,
  }}
>
  {/* ICON */}
  <PhoneInTalkOutlinedIcon
    sx={{
      color: COLORS.black,
      fontSize: 20,
      flexShrink: 0,
    }}
  />

  {/* TEXT + CHECKBOX */}
  <Box sx={{ flex: 1 }}>
   

   <FormControlLabel
  sx={{ mt: 0.5, ml: 0 }}
  control={
    <Checkbox
      size="small"
      checked={requestFollowUp === "Yes"}
      disabled={hasCalculated || isSubmitting}
      onChange={(e) => {
        const checked = e.target.checked;
        setRequestFollowUp(checked ? "Yes" : "No");

        if (checked) {
          setFollowUpError("");
        } else {
          setFollowUpError("Please confirm if you want to be contacted");
        }
      }}
      sx={{
        color: COLORS.black,
        "&.Mui-checked": { color: COLORS.black }
      }}
    />
  }
 label={
  <Typography sx={{ fontSize: 12.5 }}>
   Uncheck this box to opt out of being contacted by a specialist
  </Typography>
}
/>

{/* {followUpError && (
  <Typography sx={{ color: "red", fontSize: 12 }}>
    {followUpError}
  </Typography>
)} */}
  </Box>
  {/* <Box
  sx={{
    display: "flex",
    alignItems: "flex-start",
    gap: 1,
    background: "#FFF8E6",
    border: "1px solid #E5C07B",
    borderRadius: 1.5,
    p: 1.5,
    maxWidth: 520,
    mx: "auto",
    mb: 2
  }}
>
  <CheckCircleOutlineIcon
    sx={{
      color: COLORS.black,
      fontSize: 22,
      mt: "2px"
    }}
  />

  <Box>
    <Typography
      sx={{
        fontSize: 13.5,
        fontWeight: 600,
        color: "#000"
      }}
    >
      A Savy &amp; Sons specialist will contact you.
    </Typography>

    <Typography
      sx={{
        fontSize: 12,
        color: "#555",
        mt: 0.4
      }}
    >
      If you prefer not to be contacted, please call us at (860) 730-1518.
    </Typography>
  </Box>
</Box> */}
</Box>



      <Box textAlign="center">
    <Tooltip
  title={
    !isFormValid
      ? "Please complete all required fields to calculate results"
      : ""
  }
  arrow
>
  <span>
   
    <Button
  disabled={!isFormValid || isSubmitting ||  hasCalculated}
  onClick={handleCalculateAndSubmit}
  variant="contained"
  sx={{
    background: COLORS.black,
    color: COLORS.yellow,
    px: 6,
    opacity: isFormValid ? 1 : 0.5,
    "&:hover": {
      background: COLORS.yellow,
      color: COLORS.black
    }
  }}
>
  {hasCalculated
    ? "Calculated"
    : isSubmitting
    ? "Processing..."
    : "Calculate"}  
</Button>

  </span>
</Tooltip>
  

{/* {showSubmit && (
  <Box textAlign="center" mt={4}>
    <Tooltip
      title={
        isSubmitting
          ? "Submitting..."
          : !isFormValid
          ? "Please complete all required fields to submit"
          : ""
      }
      arrow
    >
      <span>
        <Button
          fullWidth
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid || isSubmitting}   // ✅ disable after click
          sx={{
            background: COLORS.black,
            color: COLORS.yellow,
            px: 6,
            py: 1.4,
            fontWeight: "bold",
            opacity: !isFormValid || isSubmitting ? 0.5 : 1,
            "&:hover": {
              background: COLORS.yellow,
              color: COLORS.black
            }
          }}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </Button>
      </span>
    </Tooltip>
  </Box>
)} */}


      </Box><br/>

      
         <Box textAlign="center">
      {hasCalculated &&
<Button
  onClick={hasCalculated ? handleRecalculate : handleCalculateAndSubmit}
  disabled={isSubmitting}
  variant="contained"
  sx={{
    background: COLORS.black,
    color: COLORS.yellow,
    px: 6,
    "&:hover": {
      background: COLORS.yellow,
      color: COLORS.black
    }
  }}
>
  {hasCalculated ? "Recalculate" : isSubmitting ? "Processing..." : "Calculate"}
</Button>
} </Box>
    </Box>

    {/* RESULTS */}
    {showResult && (
      <>
        <Box sx={{ background: COLORS.yellow, py: 3, mt: 5, textAlign: "center" }}>
          <Typography variant="h4" fontWeight="bold">
            I&I Cost Savings
          </Typography>
        </Box>

        {/* RATE TABLE */}
       {/* RATE TABLE */}
<Box
  sx={{
    overflowX: "auto",
    background: COLORS.lightYellow,
    mt: 2
  }}
>
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(5, 1fr)",
      minWidth: "900px",   // 👈 forces full table on mobile
      gap: 2,
      p: 2
    }}
  >
    {/* {[gpm1, gpm2, gpm3, gpm4, gpm5].map((v, i) => (
      <Box
        key={i}
        textAlign="center"
        p={2}
        sx={{
          background: "#FFF3D6",
          borderRadius: 1
        }}
      >
        <Typography fontSize={14}>
          Rate of Infiltration<br />
          (gallons per minute)
        </Typography>
        <Typography fontWeight="bold">
          {v.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </Typography>
      </Box>
    ))} */}
      {rateCards?.map((item, i) => (
      <Box
        key={i}
        textAlign="center"
        p={2}
        sx={{
          background: "#FFF3D6",
          borderRadius: 1
        }}
      >
        <Typography fontSize={14} fontWeight="bold">
          {item.label}
        </Typography>

        <Typography fontSize={13} color="text.secondary">
          ({item.unit})
        </Typography>

        <Typography fontWeight="bold" fontSize={16} mt={1}>
          {item.value.toLocaleString(undefined, {
            minimumFractionDigits: 2
          })}
        </Typography>
      </Box>
    ))}
  </Box>
</Box>


        {/* COST TABLE */}
      <Box
  sx={{
    overflowX: "auto",
    background: "#FFF8CC",
    mt: 2
  }}
>
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(5, 1fr)",
      minWidth: "900px",
      gap: 2,
      p: 2
    }}
  >
    {/* {[cost1, cost2, cost3, cost4, cost5].map((v, i) => (
      <Box key={i} textAlign="center" p={2}>
        <Typography fontSize={14}>
          Cost to Treat<br />
          ($ per minute)
        </Typography>
        <Typography color="red" fontWeight="bold">
          ${v.toFixed(2)}
        </Typography>
      </Box>
    ))} */}
     {costCards.map((item, i) => (
      <Box
        key={i}
        textAlign="center"
        p={2}
        sx={{
          background: "#FFFBE0",
          borderRadius: 1
        }}
      >
        <Typography fontSize={14} fontWeight="bold">
          {item.label}
        </Typography>

        <Typography fontSize={13} color="text.secondary">
          ({item.unit})
        </Typography>

        <Typography
          fontWeight="bold"
          fontSize={16}
          mt={1}
          sx={{ color: "red" }}
        >
          {/* ${item.value.toFixed(2)} */}
         ${item.value.toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})}

        </Typography>
      </Box>
    ))}
  </Box>
</Box>


        {/* SUMMARY */}
       {/* SUMMARY (ALWAYS 3 COLUMNS) */}
<Box
  sx={{
    overflowX: "auto",
    mt: 3
  }}
>
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      minWidth: "900px", // 👈 forces same layout on mobile
      gap: 2
    }}
  >
    {/* ONE TIME COST */}
    <Box sx={{ background: "#9BD18B", p: 3, textAlign: "center" }}>
      <Typography fontWeight="bold">
        Average Cost for Savy & Sons to Stop Infiltration
      </Typography>
      <Typography variant="h4">$5,500.00</Typography>
      <Typography
        fontWeight="bold"
        sx={{ textDecoration: "underline" }}
      >
        One Time Cost
      </Typography>
    </Box>

    {/* ROI */}
    <Box sx={{ background: "#8FCC7E", p: 3, textAlign: "center" }}>
      <Typography fontWeight="bold">ROI for Year 1</Typography>
      {/* <Typography variant="h4">{roi.toFixed(2)}%</Typography> */}
      <Typography variant="h4">{Math.floor(roi)}%</Typography>
    </Box>

    {/* PAYBACK */}
    <Box sx={{ background: "#6EA6F2", p: 3, textAlign: "center" }}>
      <Typography fontWeight="bold">
        Investment Return Timing (months)
      </Typography>
      {/* <Typography variant="h4">{paybackMonths.toFixed(2)}</Typography> */}
      <Typography variant="h4">{Math.floor(paybackMonths)}</Typography>
    </Box>
  </Box>
</Box>

        {/* PDF */}
        <Box textAlign="center" mt={4}>
          <Button
            onClick={downloadPDF}
            variant="contained"
            sx={{
              background: COLORS.black,
              color: COLORS.yellow,
              px: 6
            }}
          >
            Download PDF
          </Button>
        </Box>
      </>
    )}
    {/* ================= DISCLAIMER ================= */}
<Box
  sx={{
    mt: 5,
    px: { xs: 2, sm: 4 },
    py: 2.5,
    background: "#f9fafb",
    border: "1px solid #e4e7ec",
    borderRadius: 2,
    textAlign: "center",
  }}
>
  <Typography
    sx={{
      fontSize: 12,
      lineHeight: 1.6,
      color: "#667085",
      fontStyle: "italic",
    }}
  >
    <strong>Note:</strong> Results generated by this calculator are estimates only
    and are intended for preliminary evaluation purposes. Final pricing, savings,
    and recommendations require site-specific assessment and verification.
  </Typography>
</Box>

  </Container>
</Box>

  );
}
